import { LitElement, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { PersonStateController } from '../services/person-state.js';
import { router, navigateTo } from '../services/router.js';
import '../components/people/cp-person-list.js';
import '../components/people/cp-person-detail.js';
import '../components/people/cp-person-form.js';
import '../components/people/cp-person-import-modal.js';
import type { CpPersonForm } from '../components/people/cp-person-form.js';
import type { CpPersonImportModal } from '../components/people/cp-person-import-modal.js';
import '../components/shared/cp-page-subnav.js';

const COMM_TABS = [
  { label: 'CEO Chats', path: '/ceo-chats' },
  { label: 'Nachrichten', path: '/messages' },
  { label: 'Inbox', path: '/inbox' },
  { label: 'Kontakte', path: '/people' },
];

@customElement('cp-page-people')
export class CpPagePeople extends LitElement {
  createRenderRoot() { return this; }

  private _personState = new PersonStateController(this);

  @query('cp-person-form') private _personForm!: CpPersonForm;
  @query('cp-person-import-modal') private _importModal!: CpPersonImportModal;

  connectedCallback(): void {
    super.connectedCallback();
    router.addEventListener('route-changed', this._onRouteChanged);
    this._loadForCurrentRoute();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    router.removeEventListener('route-changed', this._onRouteChanged);
  }

  private _onRouteChanged = (): void => {
    this._loadForCurrentRoute();
    this.requestUpdate();
  };

  private _loadForCurrentRoute(): void {
    const params = router.getParams();
    if (params.id) {
      void this._personState.loadOne(Number(params.id));
    } else {
      void this._personState.loadAll();
    }
  }

  private _handlePersonSelected(e: CustomEvent<{ personId: number }>): void {
    navigateTo(`/people/${e.detail.personId}`);
  }

  private _handleCreatePerson(): void {
    this._personForm.open();
  }

  private _handleImportMarkdown(): void {
    this._importModal.open();
  }

  private _handlePersonCreated(): void {
    void this._personState.loadAll();
  }

  private _handlePersonUpdated(): void {
    const params = router.getParams();
    if (params.id) {
      void this._personState.loadOne(Number(params.id));
    }
  }

  private _handlePersonDeleted(): void {
    navigateTo('/people');
    void this._personState.loadAll();
  }

  render() {
    const params = router.getParams();
    if (params.id) {
      return this._renderDetailView(Number(params.id));
    }
    return this._renderListView();
  }

  private _renderListView() {
    const { loadingState, people, error } = this._personState;

    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${COMM_TABS} activePath="/people"></cp-page-subnav>
        <div class="cp-page-people__header">
          <div class="cp-page-people__header-row">
            <h1 class="cp-page__title">Personen</h1>
            <div class="cp-page-people__header-actions">
              <button class="cp-page-people__import-btn" @click=${this._handleImportMarkdown}>
                Markdown importieren
              </button>
              <button class="cp-page-people__add-btn" @click=${this._handleCreatePerson}>
                + Neue Person
              </button>
            </div>
          </div>
          <p class="cp-page-people__subtitle">Dein persoenliches Netzwerk und Beziehungswissen</p>
        </div>

        ${loadingState === 'loading' ? this._renderLoading() : ''}
        ${loadingState === 'error' ? this._renderError(error) : ''}
        ${loadingState === 'loaded' || loadingState === 'idle' ? html`
          <cp-person-list
            .people=${people}
            @person-selected=${this._handlePersonSelected}
          ></cp-person-list>
        ` : ''}
      </div>

      <cp-person-form @person-created=${this._handlePersonCreated}></cp-person-form>
      <cp-person-import-modal @person-created=${this._handlePersonCreated}></cp-person-import-modal>
    `;
  }

  private _renderDetailView(personId: number) {
    const { activePerson, error } = this._personState;

    if (error) {
      return html`<div class="cp-page">${this._renderError(error)}</div>`;
    }

    if (!activePerson || activePerson.id !== personId) {
      return html`
        <div class="cp-page">
          <p class="cp-page__placeholder">Lade Person...</p>
        </div>
      `;
    }

    return html`
      <div class="cp-page">
        <cp-person-detail
          .person=${activePerson}
          @person-updated=${this._handlePersonUpdated}
          @person-deleted=${this._handlePersonDeleted}
        ></cp-person-detail>
      </div>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-people__grid cp-page-people__grid--skeleton">
        ${[1, 2, 3, 4, 5, 6].map(() => html`
          <div class="cp-page-people__skeleton">
            <div class="cp-page-people__skeleton-avatar"></div>
            <div class="cp-page-people__skeleton-lines">
              <div class="cp-page-people__skeleton-line cp-page-people__skeleton-line--name"></div>
              <div class="cp-page-people__skeleton-line cp-page-people__skeleton-line--sub"></div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderError(message: string | null) {
    return html`
      <div class="cp-page-people__error">
        <p>Fehler beim Laden: ${message ?? 'Unbekannter Fehler'}</p>
        <button class="cp-page-people__retry-btn" @click=${() => this._loadForCurrentRoute()}>
          Erneut versuchen
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-people': CpPagePeople;
  }
}
